

import React from "react";
import { GoogleMap, useLoadScript, MarkerF } from "@react-google-maps/api";
import "./GooglePlacesApi.scss";

import { GOOGLE_PLACES_API_KEY } from "../../../../utils/constants";
import { useState, useEffect, useCallback } from "react";
import csc from "country-state-city";
import { parseGooglePlace } from "../../../../utils/googleMapsUtils";

const MapComponent = ({
  selectedLocation,
  containerStyle,
  onAddressChange,
  setAddressDetail,
  setSelectedLocation,
  query,
  setQuery,
  formik,
  countries,
  isLoaded,
  loadError, // Added loadError prop
}) => {
  // Removed useLoadScript from here

  const mapRef = React.useRef(null); // Ref for map instance
  const markerRef = React.useRef(null); // Ref for marker instance
  const [mapCenter, setMapCenter] = useState(
    selectedLocation || { lat: 0, lng: 0 }
  );
  const [address, setAddress] = useState("");

  const onMapLoad = useCallback((map) => {
    mapRef.current = map; // Set map instance on load
  }, []);

  // Handle the event when the map drag ends
  const handleMapDragEnd = useCallback(async () => {
    const newCenter = mapRef.current.getCenter();
    const lat = newCenter.lat();
    const lng = newCenter.lng();
    const latLng = { lat, lng };

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === "OK" && results[0]) {
        const place = results[0];
        const location = parseGooglePlace(place);

        setAddress(place.formatted_address);
        setMapCenter(latLng);
        markerRef.current.setPosition(latLng);
        setAddressDetail(location);
        setSelectedLocation(latLng);
        setQuery(location.streetAddress);

        // Update formik fields
        formik.setFieldValue("shippingaddress", location.streetAddress);
        formik.setFieldValue("city", location.city);
        formik.setFieldValue("zipcode", location.zipCode);

        if (location.country) {
          const countryOption = countries?.find((c) => c.label === location.country);
          const states = countryOption
            ? csc.getStatesOfCountry(countryOption.id).map((s) => ({ ...s, label: s.name, value: s.id }))
            : [];
          const stateOption = states?.find((s) => s.label === location.state);

          formik.setFieldValue("country", countryOption || null);
          formik.setFieldValue("state", stateOption || null);
        }
      } else {
        console.error("Geocode failed due to: " + status);
      }
    });
  }, [onAddressChange, countries, formik, setAddressDetail, setSelectedLocation, setQuery]);

  // Handle the event when the marker drag ends
  // const handleMarkerDragEnd = useCallback(async () => {
  //   const newPosition = markerRef.current.getPosition();
  //   const lat = newPosition.lat();
  //   const lng = newPosition.lng();

  //   try {
  //     const newAddress = await getAddressFromLatLng(lat, lng);
  //     setAddress(newAddress);
  //     setMapCenter({ lat, lng });
  //     mapRef.current.setCenter({ lat, lng }); // Move map to new marker position
  //     onAddressChange(newAddress); // Pass new address back to parent
  //   } catch (error) {
  //     console.error("Geocoder failed due to: " + error);
  //   }
  // }, [onAddressChange]);

  const handleMarkerDragEnd = async (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    const geocoder = new window.google.maps.Geocoder();
    const latLng = { lat, lng };

    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === "OK" && results[0]) {
        const place = results[0];
        const location = parseGooglePlace(place);

        setAddressDetail(location);
        setSelectedLocation(latLng);
        setQuery(location.streetAddress);

        // Update formik fields
        formik.setFieldValue("shippingaddress", location.streetAddress);
        formik.setFieldValue("city", location.city);
        formik.setFieldValue("zipcode", location.zipCode);

        if (location.country) {
          const countryOption = countries?.find(
            (c) => c.label === location.country
          );

          const states = countryOption
            ? csc.getStatesOfCountry(countryOption.id).map((s) => ({ ...s, label: s.name, value: s.id }))
            : [];
          const stateOption = states?.find((s) => s.label === location.state);

          formik.setFieldValue("country", countryOption || null);
          formik.setFieldValue("state", stateOption || null);
        }
      } else {
        console.error(
          "Geocode was not successful for the following reason: " + status
        );
      }
    });
  };

  // Update map center when selectedLocation changes
  useEffect(() => {
    if (selectedLocation) {
      setMapCenter(selectedLocation);
      mapRef.current?.setCenter(selectedLocation);
      markerRef.current?.setPosition(selectedLocation);
    }
  }, [selectedLocation]);

  if (loadError) return <div style={{ height: containerStyle.height, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', marginBottom: '1rem', color: '#6c757d', textAlign: 'center', padding: '20px' }}><div>Map could not be loaded. Please enter address manually below.</div></div>;
  if (!isLoaded) return <div style={{ height: containerStyle.height, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', marginBottom: '1rem', color: '#6c757d' }}>Loading Maps...</div>;

  return (
    <div className="map_section mb-3">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={13}
        onLoad={onMapLoad}
        onDragEnd={handleMapDragEnd} // Handle map drag end
      >
        <MarkerF
          position={mapCenter}
          draggable={true}
          onLoad={(marker) => (markerRef.current = marker)} // Set marker reference on load
          onDragEnd={handleMarkerDragEnd} // Handle marker drag end
          icon={"http://maps.google.com/mapfiles/ms/icons/green-dot.png"}
        />
      </GoogleMap>
      {address && <p>Address: {address}</p>}
    </div>
  );
};

export default MapComponent;
